import { useState } from "react";
import { Image, StyleSheet, View } from "react-native";

import { schoolInitials } from "@/school-identity/initials";
import type { SchoolIdentity } from "@/session/types";
import { makeStyles } from "@/theme/makeStyles";
import { useTheme } from "@/theme/useTheme";
import { Text } from "@/ui/Text";

const useStyles = makeStyles((t) =>
  StyleSheet.create({
    // Fixed box: the logo, or the initials, never change the header's layout (FR-024, FR-026).
    box: {
      flexShrink: 0,
      width: t.size.crest,
      height: t.size.crest,
      borderRadius: t.radius.pill,
      overflow: "hidden",
      backgroundColor: t.color.surfaceAlt,
    },
    image: { width: t.size.crest, height: t.size.crest },
    fallback: {
      alignItems: "center",
      justifyContent: "center",
      backgroundColor: t.color.crestBackground,
    },
  }),
);

/**
 * The school's logo at a fixed size (FR-024; R-9). Decorative: the school name sits next to it.
 * A missing or broken logo shows the school's initials in a circle of identical size (FR-026); the
 * broken state resets when the school or its logo changes.
 */
export function SchoolCrest({ school }: { school: SchoolIdentity }) {
  const styles = useStyles();
  const theme = useTheme();
  // The failure belongs to one school and logo, so a different school starts fresh (FR-026).
  const key = `${school.id}|${school.logoUrl ?? ""}`;
  const [failedKey, setFailedKey] = useState<string | null>(null);
  const showImage = Boolean(school.logoUrl) && failedKey !== key;
  return (
    <View
      style={[styles.box, !showImage && styles.fallback]}
      testID="school-crest"
      accessible={false}
      aria-hidden
      importantForAccessibility="no-hide-descendants"
    >
      {showImage ? (
        <Image
          source={{ uri: school.logoUrl as string }}
          style={styles.image}
          resizeMode="contain"
          testID="school-crest-image"
          alt=""
          onError={() => setFailedKey(key)}
        />
      ) : (
        <Text
          testID="school-crest-initials"
          variant="heading"
          weight="semiBold"
          size="sm"
          maxFontSizeMultiplier={1}
          style={{ color: theme.color.crestText }}
        >
          {schoolInitials(school.name)}
        </Text>
      )}
    </View>
  );
}
